import React, { Fragment } from 'react';

import classNames from 'classnames';
import PropTypes from 'prop-types';
import qs from 'query-string';
import { connect } from 'react-redux';
import { TransitionGroup } from 'react-transition-group';

import { Collapse } from 'wanderu-animations';
import {
    ElementQuery,
    Layout,
} from 'wanderu-components';
import {
    localize,
    selectCurrency,
    selectLocale,
} from 'wanderu-i18n';
import {
    scrollTo,
} from 'wanderu-utils';

import localeConfig from '../../../config/explorer.locales';
import Accordion from '../../components/Accordion/Accordion';
import CityCard from '../../components/CityCard/CityCard';
import events from '../../components/events';
import {
    fetchCityFromPlaceId,
    fetchCityFromUrl,
    removeOriginCity,
} from '../../ducks/OriginCities';
import { validCitiesSelector } from '../../ducks/PopularCities';
import {
    fetchLowestPrices,
    removePricings,
} from '../../ducks/Pricings';
import {
    setSelectedCity,
    removeSelectedCity,
} from '../../ducks/Status';

import styles from './Grid.scss';

// TODO:
const minCityCardWidth = 240;

class Grid extends React.Component {
    // TODO: Add propTypes checks to all components
    static defaultProps = {
        cities: [],
    };

    static async serverFetchData({
        dispatch, query = {}, params, getState,
    }) {
        const { countryState, city } = params;
        const { date, pid: placeId } = query;
        const locale = selectLocale(getState());

        if (!countryState || !city) {
            return undefined;
        }

        const originCity = placeId
            ? await dispatch(fetchCityFromPlaceId(placeId))
            : await dispatch(fetchCityFromUrl(locale, countryState, city));

        if (originCity) {
            return dispatch(
                fetchLowestPrices(originCity.wcityid, originCity.cityToCityIds, date),
            );
        }

        return undefined;
    }

    constructor(props) {
        super(props);

        this.state = {
            accordionAfter: -1,
            cityLocationChanged: false,
            fetching: false,
            selectedCity: undefined,
        };
    }

    componentDidMount() {
        const {
            city,
            countryState,
            locale,
            originCities,
            placeId,
            pricings,
        } = this.props;

        const fetching = pricings.fetching || originCities.fetching;
        if (fetching) {
            return undefined;
        }

        const { activeWcityid } = originCities;
        if (!activeWcityid) {
            return this.fetchOriginCity(locale, countryState, city, placeId);
        }

        if (activeWcityid !== pricings.activeWcityid) {
            return this.fetchPricing();
        }

        return undefined;
    }

    componentDidUpdate(prevProps) {
        const {
            cities,
            city,
            countryState,
            locale,
            originCities,
            placeId,
            pricings,
        } = this.props;
        const {
            placeId: prevPlaceId,
            countryState: prevCountryState,
            city: prevCity,
        } = prevProps;

        if (`${prevCountryState}/${prevCity}` !== `${countryState}/${city}`
            || prevPlaceId !== placeId
        ) {
            return placeId
                ? this.props.fetchCityFromPlaceId(placeId)
                : this.props.fetchCityFromUrl(locale, countryState, city);
        }

        if (originCities.activeWcityid !== prevProps.originCities.activeWcityid) {
            return this.fetchPricing();
        }

        if (!pricings.fetching
            && prevProps.pricings.fetching
            && cities.length > 0) {
            scrollTo(500, { duration: 750 });
        }

        return undefined;
    }

    componentWillUnmount() {
        this.props.removePricings();
    }

    fetchOriginCity(locale, countryState, city, placeId) {
        return placeId
            ? this.props.fetchCityFromPlaceId(placeId)
            : this.props.fetchCityFromUrl(locale, countryState, city);
    }

    fetchPricing() {
        const {
            filters,
            originCity,
            originCities,
        } = this.props;
        const { date } = filters;
        const { cityToCityIds } = originCity;
        const { activeWcityid } = originCities;

        this.props.fetchLowestPrices(activeWcityid, cityToCityIds, date);
    }

    onDimensionsChange = (dimensions) => {
        const { width } = dimensions;
        if (this.state.width === width) {
            return;
        }

        this.setState(() => ({ width }));
        this.computeAccordionPosition();
    }

    childToggleTrip = ({ date, price, wcityid }) => {
        const { selectedCity } = this.state;
        const isSelected = selectedCity === wcityid;

        events.clickCard({
            date,
            destination: wcityid,
            isExpanding: !isSelected,
            origin: this.props.originCity.wcityid,
            price,
        });

        if (isSelected) {
            return this.setState(() => ({
                accordionAfter: -1,
                selectedCity: null,
            }));
        }

        this.computeAccordionPosition(wcityid);

        this.setState(() => ({
            selectedCity: wcityid,
        }));

        return undefined;
    }

    computeAccordionPosition(wcityid = this.state.selectedCity) {
        if (!wcityid) {
            return;
        }

        const { width } = this.state;
        const { cities } = this.props;
        const cityCardIndex = cities.findIndex((city) => {
            return wcityid === city.location.wcityid;
        });

        const numCol = Math.floor(width / minCityCardWidth);
        const positionInRow = cityCardIndex % numCol + 1;
        const numCellsToEndOfRow = numCol - positionInRow;
        const numToEndOfRow = numCellsToEndOfRow + cityCardIndex;
        const numToInsertAfter = Math.min(numToEndOfRow, cities.length - 1);

        this.setState(() => ({
            accordionAfter: numToInsertAfter,
        }));
    }

    // TODO: The following functions should probably be computed directly after fetch
    // and stored in Redux
    getTripIds() {
        const { selectedCity } = this.state;

        let tripResults = {
            bus: [...new Set([
                this.getCheapestTripId('bus', selectedCity),
                this.getShortestTripId('bus', selectedCity),
            ])],
            train: [...new Set([
                this.getCheapestTripId('train', selectedCity),
                this.getShortestTripId('train', selectedCity),
            ])],
            flight: [...new Set([
                this.getCheapestTripId('flight', selectedCity),
                this.getShortestTripId('flight', selectedCity),
            ])],
        };

        // TODO: this might be too confusing. All it is doing is seeing if
        // both the tripIds in each key are truthy values, if not, delete the key
        // this code is extensible to flights/cars though which is nice
        Object.keys(tripResults).forEach((key) => {
            if (!tripResults[key].every((id) => id)) {
                delete tripResults[key];
            }
        });
        return tripResults;
    }

    getCheapestTripId(mode, wcityid) {
        const cityPricing = this.props.pricings.cities[wcityid];
        const pricingByType = cityPricing[mode] || {};
        return pricingByType?.minPriceTripId;
    }

    getShortestTripId(mode, wcityid) {
        const cityPricing = this.props.pricings.cities[wcityid];
        const pricingByType = cityPricing[mode];

        return pricingByType?.minDurationTripId;
    }

    renderCityCard(city, date, price) {
        const { wcityid } = city.location;
        const key = `selectedCity-${wcityid}`;
        return (
            <CityCard
                cityInfo={city}
                date={date}
                isSelected={wcityid === this.state.selectedCity}
                key={key}
                price={price}
                ref={(comp) => { this[key] = comp; }}
                toggleTrip={this.childToggleTrip}
            />
        );
    }

    render() {
        const {
            cities,
            currency,
            filters,
            i18nMoment,
            locale,
            originCity,
            pricings,
        } = this.props;
        const {
            accordionAfter,
            selectedCity,
        } = this.state;

        if (!originCity.formattedAddress) {
            return null;
        }

        return (
            <>
                <Layout
                    className={styles.container}
                >
                    <div className={styles.negativeMarginHack}>
                        <ElementQuery
                            onChange={this.onDimensionsChange}
                            passAsProps={false}
                            dimensionsOnly
                        />
                        {cities
                            .map((city, i) => {
                                const { wcityid } = city.location;
                                const pricing = pricings.cities[wcityid];
                                const { cheapestMode } = pricing;
                                const {
                                    USD = {},
                                    minPriceDateTime,
                                    minPriceTzOffset,
                                } = pricing[cheapestMode] || {};
                                const price = USD.min;
                                const date = minPriceDateTime
                                    ? i18nMoment(minPriceDateTime).utcOffset(minPriceTzOffset)
                                    : '';
                                let arriveCity = cities.find(
                                    ({ location }) => location.wcityid === selectedCity
                                );

                                const renderAccordion = accordionAfter === i;

                                let arrivePlaceId;
                                let arriveAddress;
                                if (renderAccordion && arriveCity) {
                                    arrivePlaceId = arriveCity.location.placeId;
                                    arriveAddress = arriveCity.location.formattedAddress;
                                }

                                return (
                                    <Fragment key={wcityid}>
                                        {this.renderCityCard(city, date, price)}
                                        <TransitionGroup className={classNames(
                                            { [styles.accordionContainer]: renderAccordion },
                                        )}>
                                            {renderAccordion && (
                                                <Collapse
                                                    duration={750}
                                                    appear
                                                >
                                                    <Accordion
                                                        arriveAddr={arriveAddress}
                                                        arrivePlaceId={arrivePlaceId}
                                                        currency={currency}
                                                        date={date}
                                                        departAddr={originCity.formattedAddress}
                                                        departPlaceId={originCity.placeId}
                                                        locale={locale}
                                                        showDate={!filters.date}
                                                        tripIds={this.getTripIds(city)}
                                                    />
                                                </Collapse>

                                            )}
                                        </TransitionGroup>
                                    </Fragment>
                                );
                            })}
                    </div>
                </Layout>
            </>
        );
    }
}

const modePriceType = PropTypes.shape({
    minDurationTripId: PropTypes.string,
    minPriceTripId: PropTypes.string,
});

Grid.propTypes = {
    cities: PropTypes.arrayOf(PropTypes.shape({
        location: PropTypes.shape({
            wcityid: PropTypes.string,
        }),
    })),
    city: PropTypes.string,
    countryState: PropTypes.string,
    currency: PropTypes.string,
    fetchCityFromPlaceId: PropTypes.func,
    fetchCityFromUrl: PropTypes.func,
    fetchLowestPrices: PropTypes.func,
    filters: PropTypes.shape({
        date: PropTypes.string,
        price: PropTypes.number,
    }),
    i18nMoment: PropTypes.func,
    locale: PropTypes.string,
    originCities: PropTypes.shape({
        activeWcityid: PropTypes.string,
        fetching: PropTypes.bool,
    }),
    originCity: PropTypes.shape({
        cityToCityIds: PropTypes.arrayOf(PropTypes.string),
        formattedAddress: PropTypes.string,
        placeId: PropTypes.string,
        wcityid: PropTypes.string,
    }),
    placeId: PropTypes.string,
    pricings: PropTypes.shape({
        activeWcityid: PropTypes.string,
        cities: PropTypes.objectOf(
            PropTypes.shape({
                bus: modePriceType,
                cheapestMode: PropTypes.oneOf(['bus', 'train', 'flight']),
                flight: modePriceType,
                train: modePriceType,
            })
        ),
        fetching: PropTypes.bool,
    }),
    removePricings: PropTypes.func,
};

function mapStateToProps(state, ownProps) {
    const explorerState = state.explorer;
    const { originCities } = explorerState;
    const {
        location,
        match,
    } = ownProps;
    const {
        countryState,
        city,
    } = match.params;
    const queries = qs.parse(location.search) || {};
    const placeId = queries.pid;

    return {
        cities: validCitiesSelector(state),
        city,
        countryState,
        currency: selectCurrency(state),
        filters: explorerState.filters,
        locale: selectLocale(state),
        noDirectService: explorerState.status.noDirectService,
        originCities,
        originCity: originCities[originCities.activeWcityid] || {},
        placeId,
        pricings: explorerState.pricings,
    };
}

const mapDispatchToProps = {
    fetchCityFromPlaceId,
    fetchCityFromUrl,
    fetchLowestPrices,
    removeOriginCity,
    removePricings,
    removeSelectedCity,
    setSelectedCity,
};

export {
    Grid,
    mapStateToProps,
};

const LocalizedGrid = localize(Grid, localeConfig.namespace);
export default connect(mapStateToProps, mapDispatchToProps)(LocalizedGrid);
